import { c as _c } from "react/compiler-runtime";
import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
const INDENT = '  ';
const MARK_CLASS = 'rounded-sm bg-accent/20';
const decodeSegment = (segment: string) => segment.replace(/~1/g, '/').replace(/~0/g, '~');

// RFC-6901 pointer → path segments (the leading "" before the first "/" is dropped).
const parsePointer = (pointer: string): string[] => pointer.split('/').slice(1).map(decodeSegment);
type RenderProps = {
  value: unknown;
  // Remaining segments to the cited node: `[]` means "this node is the target"; `null` means "off
  // the cited path".
  path: string[] | null;
  depth: number;
  markRef: RefObject<HTMLElement | null>;
};
const renderJson = ({
  value,
  path,
  depth,
  markRef
}: RenderProps): ReactNode => {
  const isTarget = path !== null && path.length === 0;
  const pad = INDENT.repeat(depth);
  const padInner = INDENT.repeat(depth + 1);
  let body: ReactNode;
  if (Array.isArray(value)) {
    body = value.length === 0 ? '[]' : <>
          {'[\n'}
          {value.map((item, index) => {
        const childPath = path && path.length > 0 && path[0] === String(index) ? path.slice(1) : null;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static pretty-print, array never reorders
          <span key={index}>
                {padInner}
                {renderJson({
              value: item,
              path: childPath,
              depth: depth + 1,
              markRef
            })}
                {index < value.length - 1 ? ',' : ''}
                {'\n'}
              </span>
        );
      })}
          {pad}
          {']'}
        </>;
  } else if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    body = entries.length === 0 ? '{}' : <>
          {'{\n'}
          {entries.map(([key, val], index) => {
        const childPath = path && path.length > 0 && path[0] === key ? path.slice(1) : null;
        return <span key={key}>
                {padInner}
                <span className="text-tertiary">{JSON.stringify(key)}</span>
                {': '}
                {renderJson({
            value: val,
            path: childPath,
            depth: depth + 1,
            markRef
          })}
                {index < entries.length - 1 ? ',' : ''}
                {'\n'}
              </span>;
      })}
          {pad}
          {'}'}
        </>;
  } else {
    body = JSON.stringify(value);
  }
  return isTarget ? <mark ref={markRef} className={MARK_CLASS}>
      {body}
    </mark> : body;
};

/**
 * Pretty-prints a decrypted JSON output with the node at `pointer` highlighted and scrolled into
 * view — the cited evidence shown in context, not as a detached snippet. The backend has already
 * validated that `pointer` resolves, so the highlight always lands on a real node.
 */
const HighlightedJson = t0 => {
  const $ = _c(9);
  const {
    value,
    pointer
  } = t0;
  const preRef = useRef(null);
  const markRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const pre = preRef.current;
      const mark = markRef.current;
      if (!pre || !mark) {
        return;
      }
      pre.scrollTop = Math.max(0, mark.offsetTop - pre.clientHeight / 3);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pointer || $[2] !== value) {
    t2 = [pointer, value];
    $[1] = pointer;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== pointer || $[5] !== value) {
    t3 = renderJson({
      value,
      path: parsePointer(pointer),
      depth: 0,
      markRef
    });
    $[4] = pointer;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <pre ref={preRef} className="relative max-h-72 overflow-auto rounded border border-tertiary bg-secondary p-3 text-snippet-2 text-primary">{t3}</pre>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export default HighlightedJson;